import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SessionData = Record<string, string | undefined>;

const STORAGE_KEY_PREFIX = 'playbook-edit-session-storage-';

export type InProgressEditSessionStorageState = {
  data: SessionData;
  update: (playbookVersionId: string, sessionId?: string) => void;
  clearAll: () => void;
};

export const createEditSessionStore = (playbookId: string) =>
  create<InProgressEditSessionStorageState>()(
    persist(
      set => ({
        data: {},
        update: (playbookVersionId: string, sessionId?: string) => {
          set(oldSession => ({
            ...oldSession,
            data: {
              ...oldSession.data,
              [playbookVersionId]: sessionId,
            },
          }));
        },
        clearAll: () => {
          set({ data: {} });
        },
      }),
      {
        version: 1,
        name: `${STORAGE_KEY_PREFIX}${playbookId}`,
      },
    ),
  );

/** Local storage hook for saving the most recent in-progress edit session ID per playbook version, in case the user navigates away with unsaved changes. */
const useInProgressEditSessionStorage = (playbookId: string) => {
  // Zustand's persist middleware handles localStorage persistence by key name, so creating a new store instance is fine.
  // store(state => state) just gets the current state (like getState()), it doesn't create a subscription.
  const store = createEditSessionStore(playbookId);
  const { data, update, clearAll } = store(state => state);

  const getEditSessionId = (playbookVersionId: string): string | undefined => {
    return data[playbookVersionId];
  };

  const clearEditSessionId = (playbookVersionId: string): void => {
    update(playbookVersionId, undefined);
  };

  const clearAllEditSessions = (): void => {
    clearAll();
  };

  return {
    getEditSessionId,
    clearEditSessionId,
    clearAllEditSessions,
  };
};

/**
 * Clears all edit session storage entries for all playbooks.
 * This should be called when the user logs out to prevent session data from persisting across user sessions.
 */
export const clearAllPlaybookEditSessions = (): void => {
  // clear across multiple tabs
  if (typeof window === 'undefined') {
    return;
  }

  // iterating through all the local storage keys and looking for ones that match the prefix
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(STORAGE_KEY_PREFIX)) {
      window.localStorage.removeItem(key);
    }
  }
};

export default useInProgressEditSessionStorage;
